/* global $ */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function VendorContactDetails() {
    const { id } = useParams(); // contact id
    const navigate = useNavigate();
    const location = useLocation();

    // Determine where to go back: vendor-details (came from vendor) or vendor-contact (came from list)
    const from = location.state?.from || 'vendor-contact';
    const fromVendorId = location.state?.vendor_id || null;

    const goBack = () => {
        if (from === 'vendor-details' && fromVendorId) {
            navigate(`/vendor-details/${fromVendorId}`);
        } else {
            navigate('/vendor-contact');
        }
    };

    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '', email: '', phone: '', designation: '', notes: ''
    });
    const [editErrors, setEditErrors] = useState({});

    // ─── Fetch Contact ────────────────────────────────────────────────────────────

    const fetchContact = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/api/vendorsContact/${id}`);
            const data = res.data.data || res.data;
            setContact(data);
            setEditForm({
                name: data.name || '',
                email: data.email || '',
                phone: data.phone || '',
                designation: data.designation || '',
                notes: data.notes || ''
            });
        } catch (error) {
            console.error("Error fetching contact:", error);
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch contact details' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchContact(); }, [id]);

    // ─── Edit Handlers ────────────────────────────────────────────────────────────

    const handleEditChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const validateEdit = () => {
        const errs = {};
        if (!editForm.name) errs.name = "Name is required";
        if (!editForm.phone) errs.phone = "Phone is required";
        setEditErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        if (!validateEdit()) return;
        setSaving(true);

        try {
            const updated_by = localStorage.getItem("user_id");
            await axiosInstance.put(`/api/vendorsContact/${id}`, {
                ...editForm,
                updated_by
            });

            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Contact updated successfully', timer: 1500, showConfirmButton: false });
            setIsEditing(false);
            fetchContact();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error!', text: error.response?.data?.message || 'Failed to update contact' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditErrors({});
        // Reset form to current contact data
        setEditForm({
            name: contact.name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            designation: contact.designation || '',
            notes: contact.notes || ''
        });
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This contact will be permanently deleted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/vendorsContact/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Contact deleted.', timer: 1500, showConfirmButton: false });
                goBack();
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to delete contact' });
            }
        }
    };

    // ─── Helper ───────────────────────────────────────────────────────────────────

    const InfoRow = ({ label, value }) => (
        <div className="col-md-6 mb-3">
            <label className="text-muted small mb-1 d-block">{label}</label>
            <span className="fw-medium">{value || <span className="text-muted">—</span>}</span>
        </div>
    );

    // ─── Loading / Not found ──────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!contact) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-danger">Contact not found.</div>
                </div>
                <Footer />
            </div>
        );
    }

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <button
                                    className="btn btn-sm btn-outline-secondary me-3"
                                    onClick={goBack}
                                >
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>
                                <div>
                                    <h5 className="mb-0">{contact.name}</h5>
                                    {contact.designation && (
                                        <small className="text-muted">{contact.designation}</small>
                                    )}
                                </div>
                                <span className={`badge ms-3 ${contact.isactive ? 'bg-success' : 'bg-danger'}`}>
                                    {contact.isactive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="d-flex gap-2">
                                {!isEditing && (
                                    <>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            <i className="fa fa-edit me-1"></i> Edit
                                        </button>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={handleDelete}
                                        >
                                            <i className="fa fa-trash-alt me-1"></i> Delete
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb" className="mb-3">
                        <ol className="breadcrumb">
                            {from === 'vendor-details' && fromVendorId ? (
                                <>
                                    <li className="breadcrumb-item">
                                        <span
                                            style={{ cursor: 'pointer', color: '#007bff' }}
                                            onClick={() => navigate('/vendor')}
                                        >
                                            Vendors
                                        </span>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <span
                                            style={{ cursor: 'pointer', color: '#007bff' }}
                                            onClick={() => navigate(`/vendor-details/${fromVendorId}`)}
                                        >
                                            Vendor Details
                                        </span>
                                    </li>
                                </>
                            ) : (
                                <li className="breadcrumb-item">
                                    <span
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => navigate('/vendor-contact')}
                                    >
                                        Vendor Contacts
                                    </span>
                                </li>
                            )}
                            <li className="breadcrumb-item active">{contact.name}</li>
                        </ol>
                    </nav>

                    <div className="card mb-0">
                        <div className="card-body">

                            {/* ── View Mode ── */}
                            {!isEditing ? (
                                <div>
                                    <h6 className="text-primary border-bottom pb-2 mb-3">Contact Information</h6>
                                    <div className="row">
                                        <InfoRow label="Full Name" value={contact.name} />
                                        <InfoRow label="Designation" value={contact.designation} />
                                        <InfoRow
                                            label="Email"
                                            value={
                                                contact.email
                                                    ? <a href={`mailto:${contact.email}`} className="text-primary">{contact.email}</a>
                                                    : null
                                            }
                                        />
                                        <InfoRow label="Phone" value={contact.phone} />
                                        <InfoRow
                                            label="Status"
                                            value={
                                                <span className={`badge ${contact.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                    {contact.isactive ? 'Active' : 'Inactive'}
                                                </span>
                                            }
                                        />
                                    </div>

                                    {contact.notes && (
                                        <>
                                            <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Notes</h6>
                                            <p className="text-muted">{contact.notes}</p>
                                        </>
                                    )}

                                    {/* Vendor Reference */}
                                    {contact.vendor && (
                                        <>
                                            <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Associated Vendor</h6>
                                            <div className="row">
                                                <InfoRow label="Vendor Name" value={
                                                    <span
                                                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                        onClick={() => navigate(`/vendor-details/${contact.vendor_id}`)}
                                                    >
                                                        {contact.vendor.name}
                                                    </span>
                                                } />
                                                <InfoRow label="Vendor Phone" value={contact.vendor.phone} />
                                                <InfoRow label="Vendor Email" value={contact.vendor.email} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                /* ── Edit Mode ── */
                                <div>
                                    <h6 className="text-primary border-bottom pb-2 mb-3">Edit Contact</h6>
                                    <form onSubmit={handleSaveEdit}>
                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <label>Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="name"
                                                    value={editForm.name}
                                                    onChange={handleEditChange}
                                                />
                                                {editErrors.name && <small className="text-danger">{editErrors.name}</small>}
                                            </div>

                                            <div className="col-md-4 mb-3">
                                                <label>Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    name="email"
                                                    value={editForm.email}
                                                    onChange={handleEditChange}
                                                />
                                            </div>

                                            <div className="col-md-4 mb-3">
                                                <label>Phone *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="phone"
                                                    value={editForm.phone}
                                                    onChange={handleEditChange}
                                                />
                                                {editErrors.phone && <small className="text-danger">{editErrors.phone}</small>}
                                            </div>

                                            <div className="col-md-4 mb-3">
                                                <label>Designation</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="designation"
                                                    value={editForm.designation}
                                                    onChange={handleEditChange}
                                                />
                                            </div>

                                            <div className="col-md-12 mb-3">
                                                <label>Notes</label>
                                                <textarea
                                                    className="form-control"
                                                    name="notes"
                                                    rows={3}
                                                    value={editForm.notes}
                                                    onChange={handleEditChange}
                                                />
                                            </div>
                                        </div>

                                        <div className="text-end mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-secondary me-2"
                                                onClick={handleCancelEdit}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={saving}
                                            >
                                                {saving ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                        </div>
                    </div>

                </div>
                <Footer />
            </div>
        </div>
    );
}

export default VendorContactDetails;